import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const LanguageLearning = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'language'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container language-learning-page">
      <h1 className="page-title">Language Learning</h1>

      {/* Include the image */}
      <img
        src="/images/wf-langauges.webp"
        alt="Language Learning"
        className="page-image"
      />

      <p className="page-intro">
        Learning a new language is a powerful way to expand your mind, improve cognitive health, and deepen cultural understanding.
      </p>

      <p className="page-text">
        This section explores the benefits of language learning, practical methods to start, and strategies to stay motivated.
      </p>

      <hr className="page-divider" />

      <h2 className="page-heading">Health Benefits of Language Learning</h2>
      <ul className="content-list">
        <li><strong>Boosts Brain Power:</strong> Learning a language enhances problem-solving and critical thinking skills.</li>
        <li><strong>Improves Memory:</strong> Memorizing vocabulary and grammar rules strengthens overall memory function.</li>
        <li><strong>Delays Cognitive Decline:</strong> Bilingualism may help prevent dementia and Alzheimer’s disease.</li>
        <li><strong>Enhances Multitasking Abilities:</strong> Switching between languages improves cognitive flexibility.</li>
        <li><strong>Expands Cultural Awareness:</strong> Learning a language fosters understanding of different cultures.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="page-heading">Curated Video Collection</h2>
      <p className="page-text">Explore these videos to learn more about language learning:</p>

      {loading ? (
        <p className="loading-message">Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-actions">
                  {/* ✅ Helpful Button */}
                  <button
                    className="button"
                    onClick={() => handleMarkHelpful(video._id)}
                    disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  >
                    {video.usersWhoClickedHelpful?.includes(user?.id)
                      ? `Helpful (${video.helpfulCount})`
                      : `Mark as Helpful (${video.helpfulCount || 0})`}
                  </button>
                </div>
                <hr />
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}


      <hr className="page-divider" />

      <h3 className="subheading">Join the Language Learning Community</h3>
      <p className="page-text">
        Have questions or tips about learning a new language? Connect with fellow learners in our forum.
      </p>
      <a href="/forum/Languages" className="btn">Join the Discussion</a>
    </div>
  );
};

export default LanguageLearning;
